import { getAccessToken } from './getAccessToken';
import moment from 'moment';
import { ENVIRONMENT_URL, DATAVERSE_TABLES } from '../constants/dataverse';
import { fetchQualityTourRecords } from './cycleService';
import { normalizeGuid } from '../utils/guid';

const environmentUrl = ENVIRONMENT_URL;

export interface ProcessFormatRecordData {
  cr3ea_qualitytourid: string;
  cr3ea_title: string;
  cr3ea_cycle: string;
  cr3ea_shift: string | null;
  cr3ea_tourstartdate: string;
  cr3ea_observedby: string | null;
  cr3ea_productname: string;
  cr3ea_executivename: string;
  cr3ea_lineno: string | null;
  cr3ea_itemname: string | null;
  cr3ea_qtyused: string | null;
  cr3ea_status: string | null;
  cr3ea_variance: string | null;
  cr3ea_noofbiscuits: string | null;
}

export interface SaveResponse {
  success: boolean;
  message: string;
  data?: any;
}

function getShiftValue(): string {
  if (typeof localStorage === 'undefined') return 'shift 1';
  return localStorage.getItem('shiftValue') || 'shift 1';
}

export function collectProcessFormatRecord(
  cycleNum: number,
  product: string,
  executiveName: string,
  lineNo: string | null,
  itemname: string | null,
  biscuits: number | null,
  qtyUsed: number | null,
  status: string | null,
  variance: string | null,
  QualityTourId: string,
  UserName: string | null
): ProcessFormatRecordData {
  return {
    cr3ea_qualitytourid: normalizeGuid(QualityTourId),
    cr3ea_title: 'CycleData_' + moment().format('MM-DD-YYYY'),
    cr3ea_cycle: `Cycle-${cycleNum}`,
    cr3ea_shift: getShiftValue(),
    cr3ea_tourstartdate: moment().format('MM-DD-YYYY'),
    cr3ea_observedby: UserName || null,
    cr3ea_productname: product || 'N/A',
    cr3ea_executivename: executiveName || 'N/A',
    cr3ea_lineno: lineNo || null,
    cr3ea_itemname: itemname || null,
    cr3ea_qtyused: qtyUsed != null ? String(qtyUsed) : null,
    cr3ea_status: status || null,
    cr3ea_variance: variance || null,
    cr3ea_noofbiscuits: biscuits != null ? String(biscuits) : null,
  };
}

export async function savesectionApicall(
  data: ProcessFormatRecordData[],
  optionalAccessToken?: string
): Promise<SaveResponse> {
  try {
    const buttons = document.querySelectorAll("button");
    buttons.forEach(button => (button as HTMLButtonElement).disabled = true);

    let accessToken: string | undefined = optionalAccessToken;
    if (!accessToken) {
      const tokenResult = await getAccessToken();
      accessToken = tokenResult?.token;
    }
    if (!accessToken) {
      throw new Error("Access token is invalid or missing");
    }

    const headers = {
      "Accept": "application/json",
      "Content-Type": "application/json; charset=utf-8",
      "OData-MaxVersion": "4.0",
      "OData-Version": "4.0",
      "Prefer": "return=representation",
      "Authorization": `Bearer ${accessToken}`,
    };

    const apiVersion = "9.2";
    const tableName = DATAVERSE_TABLES.PROCESS_FORMAT;
    const apiUrl = `${environmentUrl}/api/data/v${apiVersion}/${tableName}`;

    const results: any[] = [];
    for (const record of data) {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers,
        body: JSON.stringify(record),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to save record: ${response.status} - ${errorText}`);
      }
      const result = await response.json();
      results.push(result);
    }

    buttons.forEach(button => (button as HTMLButtonElement).disabled = false);
    return {
      success: true,
      message: `${data.length} records saved successfully`,
      data: results,
    };
  } catch (error) {
    console.error("Error saving Process Format records:", error);
    const buttons = document.querySelectorAll("button");
    buttons.forEach(button => (button as HTMLButtonElement).disabled = false);
    return {
      success: false,
      message: error instanceof Error ? error.message : "Failed to save records. Please try again.",
      data: [],
    };
  }
}

export interface CycleItemData {
  itemname: string;
  noofbiscuits: string;
  qtyused: string;
  status: string;
  variance: string;
}

export interface ProcessFormatCycleData {
  cycleNum: string;
  product: string;
  executiveName: string;
  lineNo: string;
  tourstartdate: string;
  items: CycleItemData[];
}

export async function fetchCycleData(
  QualityTourId: string,
  optionalAccessToken?: string | null
): Promise<ProcessFormatCycleData[]> {
  try {
    const selectFields = [
      "cr3ea_cycle",
      "cr3ea_productname",
      "cr3ea_executivename",
      "cr3ea_lineno",
      "cr3ea_tourstartdate",
      "cr3ea_itemname",
      "cr3ea_noofbiscuits",
      "cr3ea_qtyused",
      "cr3ea_status",
      "cr3ea_variance",
    ];

    const records = await fetchQualityTourRecords({
      tableName: DATAVERSE_TABLES.PROCESS_FORMAT,
      selectFields,
      qualityTourId: QualityTourId,
      optionalAccessToken,
    });

    const byKey: Record<string, ProcessFormatCycleData> = {};

    records.forEach((record: Record<string, unknown>) => {
      const cycleNum = String(record.cr3ea_cycle || '').replace('Cycle-', '').trim();
      if (!cycleNum) return;

      const key = `${cycleNum}|${record?.cr3ea_productname ?? ''}|${record?.cr3ea_executivename ?? ''}|${record?.cr3ea_lineno ?? ''}`;
      if (!byKey[key]) {
        byKey[key] = {
          cycleNum,
          product: String(record?.cr3ea_productname ?? ''),
          executiveName: String(record?.cr3ea_executivename ?? ''),
          lineNo: String(record?.cr3ea_lineno ?? ''),
          tourstartdate: String(record?.cr3ea_tourstartdate ?? ''),
          items: [],
        };
      }
      byKey[key].items.push({
        itemname: String(record?.cr3ea_itemname ?? ''),
        noofbiscuits: String(record?.cr3ea_noofbiscuits ?? ''),
        qtyused: String(record?.cr3ea_qtyused ?? ''),
        status: String(record?.cr3ea_status ?? ''),
        variance: String(record?.cr3ea_variance ?? ''),
      });
    });

    return Object.values(byKey);
  } catch (error) {
    console.error("Error fetching Process Format cycle data:", error);
    return [];
  }
}
